<app-page-header
        [subtitle]="rolloutDetail ? rolloutDetail.rollout.studioName + ' - ' + rolloutDetail.rollout.targetReleaseVersion : ''"
        [title]="'Rollout Detail'">
</app-page-header>

<div class="flex items-center gap-2">
    <p-button icon="pi pi-arrow-left" label="Back to Firmware Rollouts" routerLink="/firmware-rollouts" text></p-button>
    @if (this.canCancel) {
        <p-button
                (click)="this.cancelRollout()"
                [loading]="this.cancellingRollout"
                icon="pi pi-times-circle"
                label="Cancel Rollout"
                severity="danger"
                text>
        </p-button>
    }
    @if (this.refreshing) {
        <i class="pi pi-spin pi-spinner text-gray-400 text-sm" title="Refreshing..."></i>
    }
</div>

@if (rolloutDetail) {
    <div class="my-4 border border-gray-300 dark:border-gray-700 rounded-sm p-4">
        <div class="flex gap-6 flex-wrap items-center mb-3">
            <p-tag
                    [severity]="rolloutDetail.rollout.status === RolloutStatus.COMPLETED ? 'success'
                        : rolloutDetail.rollout.status === RolloutStatus.COMPLETED_WITH_ERRORS ? 'danger'
                        : rolloutDetail.rollout.status === RolloutStatus.CANCELLED ? 'secondary'
                        : rolloutDetail.rollout.status === RolloutStatus.PENDING ? 'warn'
                        : 'info'"
                    [value]="rolloutDetail.rollout.status | enumLabel">
            </p-tag>
            <div><span class="font-semibold">Triggered By:</span> {{ rolloutDetail.rollout.triggeredBy }}</div>
            <div><span
                    class="font-semibold">Triggered At:</span> {{ rolloutDetail.rollout.triggeredAt | formatDateTime }}
            </div>
        </div>
        @if (this.phaseLabel()) {
            <div class="text-sm text-blue-500 dark:text-blue-300 mb-2">
                <i class="pi pi-spin pi-spinner mr-1"></i>
                {{ this.phaseLabel() }}
            </div>
        }
        @if (rolloutDetail.rollout.status === RolloutStatus.CANCELLED && rolloutDetail.rollout.sampleCounts.inProgress > 0) {
            <div class="text-sm text-gray-500 dark:text-gray-400 mb-2">
                <i class="pi pi-info-circle mr-1"></i>
                Cancelled - no further batches will be dispatched. The sample(s) below were already
                mid-flash when this was cancelled and are still finishing up on their own.
            </div>
        }
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
            <span class="text-gray-500 dark:text-gray-400">{{ rolloutDetail.rollout.sampleCounts.total }} samples</span>
            @if (rolloutDetail.rollout.sampleCounts.pending > 0) {
                <span class="text-gray-500 dark:text-gray-400">{{ rolloutDetail.rollout.sampleCounts.pending }}
                    pending</span>
            }
            @if (rolloutDetail.rollout.sampleCounts.inProgress > 0) {
                <span class="text-blue-500 dark:text-blue-300">{{ rolloutDetail.rollout.sampleCounts.inProgress }}
                    {{ rolloutDetail.rollout.status === RolloutStatus.CANCELLED ? 'still finishing' : 'in progress' }}</span>
            }
            @if (rolloutDetail.rollout.sampleCounts.success > 0) {
                <span class="text-green-500 dark:text-green-300">{{ rolloutDetail.rollout.sampleCounts.success }}
                    successful</span>
            }
            @if (rolloutDetail.rollout.sampleCounts.error > 0) {
                <span class="text-red-500 dark:text-red-300">{{ rolloutDetail.rollout.sampleCounts.error }}
                    failed</span>
                <p-button
                        (click)="this.retryFailedSamples()"
                        [loading]="this.retryingFailedSamples"
                        icon="pi pi-refresh"
                        label="Retry Failed Samples"
                        severity="warn"
                        size="small">
                </p-button>
            }
            @if (rolloutDetail.rollout.sampleCounts.cancelled > 0) {
                <span class="text-gray-500 dark:text-gray-400">{{ rolloutDetail.rollout.sampleCounts.cancelled }}
                    cancelled</span>
            }
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div
                    [ngClass]="rolloutDetail.rollout.sampleCounts.error > 0 ? 'bg-orange-500' : 'bg-blue-500'"
                    [style.width.%]="this.progressPercent()"
                    class="h-full">
            </div>
        </div>
    </div>

    <p-table [loading]="this.loading" [value]="this.sortedSamples">
        <ng-template pTemplate="header">
            <tr>
                <th>MAC Address</th>
                <th>Status</th>
                <th>Started At</th>
                <th>Finished At</th>
                <th></th>
            </tr>
        </ng-template>
        <ng-template let-sample pTemplate="body">
            <tr [ngClass]="sample.currentStatus === RolloutSampleStatus.ERROR ? 'bg-red-50 dark:bg-red-950/40' : ''">
                <td>
                    {{ sample.macAddress }}
                    @if (sample.pilot) {
                        <span class="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Pilot</span>
                    }
                </td>
                <td>
                    <p-tag
                            [severity]="sample.currentStatus === RolloutSampleStatus.SUCCESS ? 'success'
                                : sample.currentStatus === RolloutSampleStatus.ERROR ? 'danger'
                                : sample.currentStatus === RolloutSampleStatus.CANCELLED ? 'secondary'
                                : 'info'"
                            [value]="sample.currentStatus | enumLabel">
                    </p-tag>
                </td>
                <td>{{ sample.startedAt | formatDateTime }}</td>
                <td>{{ sample.finishedAt | formatDateTime }}</td>
                <td>
                    <p-button
                            (click)="this.toggleSampleHistory(sample.physicalSampleId)"
                            [icon]="sample.currentErrorMessage ? 'pi pi-exclamation-triangle' : 'pi pi-history'"
                            [label]="this.expandedSampleId === sample.physicalSampleId ? 'Hide History' : 'View History'"
                            [severity]="sample.currentErrorMessage ? 'danger' : undefined"
                            text>
                    </p-button>
                </td>
            </tr>
            @if (this.expandedSampleId === sample.physicalSampleId) {
                <tr>
                    <td colspan="5">
                        @if (sample.currentErrorMessage) {
                            <p class="text-red-500 dark:text-red-300 mb-2">
                                <span class="font-semibold">Error:</span> {{ sample.currentErrorMessage }}
                            </p>
                        }
                        @if (this.loadingHistoryForSampleId === sample.physicalSampleId) {
                            <span>Loading history...</span>
                        } @else {
                            <ul>
                                @for (event of this.historyBySampleId.get(sample.physicalSampleId); track event.occurredAt) {
                                    <li>
                                        {{ event.occurredAt | formatDateTime }} - {{ event.processState | enumLabel }}
                                        @if (event.errorMessage) {
                                            : {{ event.errorMessage }}
                                        }
                                    </li>
                                }
                            </ul>
                        }
                    </td>
                </tr>
            }
        </ng-template>
    </p-table>
}
